import {
  WEBFLOW_CONTENT_WRAPPER_CLASS,
  WEBFLOW_CONTENT_WRAPPER_SELECTOR,
} from './extract-content';

/**
 * Injected ONLY on Webflow hybrid pages (SSR gated by featureFlags.webflow +
 * connected integration), before scoped Webflow CSS.
 *
 * Cuts Tradewing theme inheritance at the content island without using
 * `all: revert` — that resets layout (display/position/margin/padding) and
 * breaks Webflow hover pills, dropdowns, and nav alignment.
 *
 * Double-class scoped to `.w-webflow-tradewing-content` — never applies to
 * CMS website builder or standard TW pages when Webflow is off.
 *
 * Image sizing is handled by promoting HTML width/height attributes to inline
 * styles during extract (see promoteImgDimensionAttributes).
 */
export const WEBFLOW_CHAKRA_ISOLATION_CSS = `
${WEBFLOW_CONTENT_WRAPPER_SELECTOR} {
  font-family: initial;
  font-size: initial;
  font-weight: initial;
  font-style: initial;
  line-height: initial;
  letter-spacing: initial;
  text-transform: initial;
  color: initial;
  background: transparent;
  overflow: visible;
}

${WEBFLOW_CONTENT_WRAPPER_SELECTOR},
${WEBFLOW_CONTENT_WRAPPER_SELECTOR} * {
  box-sizing: border-box;
}

/* Typography / link preflight only — leave Webflow layout props alone. */
${WEBFLOW_CONTENT_WRAPPER_SELECTOR} :where(h1, h2, h3, h4, h5, h6, p, a, button, input, textarea, select, label, li, span) {
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  font-style: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  text-decoration: none;
  color: inherit;
}

/* Keep nav / dropdown layers from being clipped by TW wrappers. */
${WEBFLOW_CONTENT_WRAPPER_SELECTOR} :where(.w-nav, .w-nav-menu, .w-dropdown, .w-dropdown-list, nav, [class*="nav"], [class*="dropdown"]) {
  overflow: visible;
}
`.trim();

/**
 * Belt-and-suspenders override after scoped CSS. Must NOT use :where() — that
 * zeros specificity so Webflow's hover padding rule still wins.
 *
 * Root cause: Webflow sets ≥1440px resting padding to var(--_theme---space--m)
 * (16px) but `:hover { padding-left: 8px }`, which shifts text left.
 * `scopeWebflowCss` also strips those hover padding decls.
 */
export const buildWebflowHoverAlignCss = (wrapperSelector: string): string =>
  `
@media screen and (min-width: 1440px) {
  ${wrapperSelector} .navbar_dropdown-link:hover,
  ${wrapperSelector} .navbar_dropdown-link:focus,
  ${wrapperSelector} .navbar_dropdown-link:focus-visible {
    padding-left: var(--_theme---space--m, 1rem) !important;
    padding-right: var(--_theme---space--m, 1rem) !important;
  }
}
`.trim();

export const WEBFLOW_HOVER_ALIGN_CSS = buildWebflowHoverAlignCss(
  WEBFLOW_CONTENT_WRAPPER_SELECTOR
);

export { WEBFLOW_CONTENT_WRAPPER_CLASS, WEBFLOW_CONTENT_WRAPPER_SELECTOR };
